import { useMutation } from '@apollo/client';
import { ErrorMessage } from '@hookform/error-message';
import { AdminFeature } from '@website/components/admin/AdminFeature';
import BootstrapTypeahead from '@website/components/forms/fields/BootstrapTypeahead';
import PriceField from '@website/components/forms/fields/PriceField';
import { SubmitButton } from '@website/components/forms/fields/SubmitButton';
import { AlertDismissible } from '@website/components/utilities/AlertDismissible';
import ErrorPage from '@website/components/utilities/ErrorPage';
import {
  TripDetailsForTripEditFormQuery,
  UpdateTripAdminSettingsDocument,
  UpdateTripSettingsInput,
} from '@website/types/graphqlOperations';
import { useState } from 'react';
import { Alert, Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import slugify from 'slugify';

type AdminTripFormFieldsProps = {
  trip: TripDetailsForTripEditFormQuery['tripByURL'];
};

export default function AdminTripFormFields({
  trip,
}: AdminTripFormFieldsProps) {
  const [successMessage, setSuccessMessage] = useState<string>();
  const [showPromotionsBanner, setShowPromotionsBanner] = useState<boolean>(
    () => !!trip.pageSettings?.promotionsBanner,
  );

  const {
    control,
    formState,
    handleSubmit,
    register,
    trigger,
    watch,
    setValue,
  } = useForm<UpdateTripSettingsInput>({
    mode: 'onBlur',
    defaultValues: {
      id: trip.id,
      active: trip.active === 'ACTIVE',
      isRecentTrip: trip.isRecentTrip,
      preventGuestCancellation: trip.preventGuestCancellation,
      preventGuestCancellationMessage: trip.preventGuestCancellationMessage,
      addAPhantomGroup: trip.addAPhantomGroup,
      overlayText: trip.overlayText,
      phantomGroupPrice: trip.phantomGroupPrice,
      urlSlug: trip.urlSlug,
      tags: trip.tags,
      pageSettings: trip.pageSettings,
    },
  });

  const preventGuestCancellation = watch('preventGuestCancellation');
  const addAPhantomGroup = watch('addAPhantomGroup');

  const { errors, isSubmitting } = formState;

  const [updateTrip, { loading, error }] = useMutation(
    UpdateTripAdminSettingsDocument,
  );

  async function submitForm(formData: Omit<UpdateTripSettingsInput, 'active'>) {
    // trigger validation manually, this is to show error messages on our custom
    // controls
    await trigger();

    // this prevents submissions if the form isn't valid
    if (errors && Object.keys(errors)[0]) {
      return;
    }

    await updateTrip({
      variables: {
        input: {
          ...formData,
          pageSettings: {
            ...(formData.pageSettings ?? {}),
            recommendedTrips: formData.pageSettings?.recommendedTrips.filter(
              (recommendation) => recommendation.title && recommendation.url,
            ),
          },
        },
      },
    });

    setSuccessMessage('Your trip has been updated successfully!');
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <>
      {successMessage && (
        <Row>
          <Col>
            <AlertDismissible
              variant="success"
              title="Trip Edit Form"
              message={successMessage}
            />
          </Col>
        </Row>
      )}
      <Form onSubmit={handleSubmit(submitForm)}>
        <AdminFeature>
          <Form.Group controlId="urlSlug">
            <Form.Label>Url Slug *</Form.Label>
            <Form.Control
              type="text"
              {...register('urlSlug', {
                required: true,
                onBlur(event) {
                  const { value } = event.target;
                  const urlSlug = slugify(value, { lower: true });
                  setValue('urlSlug', urlSlug);
                },
              })}
            />
            <ErrorMessage
              errors={errors}
              name="urlSlug"
              render={({ message }) => (
                <Form.Text as="p" className="text-danger">
                  {message ?? 'Url Slug is required!'}
                </Form.Text>
              )}
            />
            <Alert variant="info" className="mt-2">
              <p>
                The url slug is going to be the unique identifier for your trip.
                Based on the title, it will be automatically generated. You can
                change it if you want to.
              </p>
              <p>
                Based on the url slug you entered, your trip full url will be:{' '}
                <strong>
                  https://www.skyhookadventure.com/trips/{watch('urlSlug')}
                </strong>
              </p>
            </Alert>
          </Form.Group>

          <Form.Group controlId="tags">
            <Form.Label>Tags</Form.Label>
            <BootstrapTypeahead
              name="tags"
              placeholder="Add tags for this trip"
              control={control}
              ariaLabel="Tags"
              options={[]}
              multiple
              allowNew
            />

            <ErrorMessage
              errors={errors}
              name="tags"
              render={({ message }) => (
                <Form.Text as="p" className="text-danger">
                  {message}
                </Form.Text>
              )}
            />
          </Form.Group>

          <Form.Group controlId="isActive">
            <Form.Label>Active</Form.Label>
            <br />
            <Form.Text>
              A trip won't be available to book, until it is set as active. When
              you activate a trip, it will take a few minutes to appear on the
              trip search page.
              <br />
              Note that if you are instead disabling a trip, it won't impact
              existing bookings (which will still show for the guest and host).
            </Form.Text>
            <Form.Switch name="active" {...register('active')} />
            <ErrorMessage
              errors={errors}
              name="active"
              render={({ message }) => (
                <Form.Text as="p" className="text-danger">
                  {message}
                </Form.Text>
              )}
            />
          </Form.Group>

          <Form.Group controlId="isRecentTrip">
            <Form.Label>Show in recent trips?</Form.Label>
            <br />
            <Form.Text>
              When this is enabled, this will show the trip in the recent trips
              section of the homepage.
            </Form.Text>
            <Form.Switch name="isRecentTrip" {...register('isRecentTrip')} />
            <ErrorMessage
              errors={errors}
              name="isRecentTrip"
              render={({ message }) => (
                <Form.Text as="p" className="text-danger">
                  {message}
                </Form.Text>
              )}
            />
          </Form.Group>

          <Form.Group controlId="customTextOverlay">
            <Form.Label>Custom Text Overlay</Form.Label>
            <br />
            <Form.Text>
              The text added here will be displayed on the trip header image.
            </Form.Text>
            <Form.Control
              name="overlayText"
              type="text"
              {...register('overlayText')}
            />
            <ErrorMessage
              errors={errors}
              name="overlayText"
              render={({ message }) => (
                <Form.Text as="p" className="text-danger">
                  {message}
                </Form.Text>
              )}
            />
          </Form.Group>

          <Form.Group controlId="preventGuestCancellation">
            <Form.Label>Prevent Customer from Cancelling</Form.Label>
            <br />
            <Form.Text>
              When this is enabled, the customer will not be able to cancel the
              trip and will need to contact us to cancel.
            </Form.Text>
            <Form.Switch
              name="active"
              {...register('preventGuestCancellation')}
            />
            <ErrorMessage
              errors={errors}
              name="preventGuestCancellation"
              render={({ message }) => (
                <Form.Text as="p" className="text-danger">
                  {message}
                </Form.Text>
              )}
            />
          </Form.Group>

          {preventGuestCancellation && (
            <Form.Group controlId="preventGuestCancellationNotice">
              <Form.Label>
                Message to show when customer tries to cancel
              </Form.Label>
              <Form.Control
                as="textarea"
                type="text"
                required
                {...register('preventGuestCancellationMessage', {
                  required: true,
                })}
              />
              <ErrorMessage
                errors={errors}
                name="preventGuestCancellationMessage"
                render={({ message }) => (
                  <Form.Text as="p" className="text-danger">
                    {message}
                  </Form.Text>
                )}
              />
            </Form.Group>
          )}
          <Form.Group controlId="addAPhantomGroup">
            <Form.Label>
              Allow trip to appear in search even without active groups
            </Form.Label>
            <br />
            <Form.Text>
              When this is enabled, the trip will appear in the search results
              even if there are no active groups in the future.
            </Form.Text>
            <Form.Switch
              name="addAPhantomGroup"
              {...register('addAPhantomGroup')}
            />
            <ErrorMessage
              errors={errors}
              name="addAPhantomGroup"
              render={({ message }) => (
                <Form.Text as="p" className="text-danger">
                  {message}
                </Form.Text>
              )}
            />
          </Form.Group>

          {addAPhantomGroup && (
            <Form.Group controlId="phantomGroupPrice">
              <Form.Label>
                Price to display for Trip when no active groups are available
              </Form.Label>
              <PriceField
                name="phantomGroupPrice"
                currency={trip.host.paymentDetails?.currency}
                control={control}
              />
              <ErrorMessage
                errors={errors}
                name="phantomGroupPrice"
                render={({ message }) => (
                  <Form.Text as="p" className="text-danger">
                    {message}
                  </Form.Text>
                )}
              />
            </Form.Group>
          )}

          <Form.Group controlId="pageSettings.promotionsBanner">
            <Form.Label>Show Promotion Banner</Form.Label>
            <br />
            <Form.Text>
              When this is enabled, the promotion banner will be shown on the
              trip page.
            </Form.Text>
            <Form.Switch
              name="active"
              checked={showPromotionsBanner}
              onChange={(e) => {
                setShowPromotionsBanner((prev) => !prev);
                if (!e.target.checked) {
                  setValue('pageSettings.promotionsBanner', undefined);
                }
              }}
            />
          </Form.Group>

          {showPromotionsBanner && (
            <>
              <Form.Group controlId="promotionsBanner">
                <Form.Label>Promotions Banner Text</Form.Label>
                <Form.Control
                  as="textarea"
                  type="text"
                  {...register('pageSettings.promotionsBanner.text')}
                />
                <ErrorMessage
                  errors={errors}
                  name="pageSettings.promotionsBanner.text"
                  render={({ message }) => (
                    <Form.Text as="p" className="text-danger">
                      {message}
                    </Form.Text>
                  )}
                />
              </Form.Group>
              <Form.Group controlId="promotionsBanner">
                <Form.Label>Promotions Banner Button Link</Form.Label>
                <Form.Control
                  type="text"
                  {...register('pageSettings.promotionsBanner.link')}
                />
                <ErrorMessage
                  errors={errors}
                  name="pageSettings.promotionsBanner"
                  render={({ message }) => (
                    <Form.Text as="p" className="text-danger">
                      {message}
                    </Form.Text>
                  )}
                />
              </Form.Group>
            </>
          )}

          <Row>
            <Col xs={12} className="mt-3">
              <Form.Label>Recommended Trips</Form.Label>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Group controlId="recommendedTrips-0-title">
                <Form.Label>Title</Form.Label>

                <Form.Control
                  type="text"
                  {...register('pageSettings.recommendedTrips.0.title')}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="recommendedTrips-0-url">
                <Form.Label>URL</Form.Label>
                <Form.Control
                  type="url"
                  {...register('pageSettings.recommendedTrips.0.url')}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col>
              <Form.Group controlId="recommendedTrips-1-title">
                <Form.Label>Title</Form.Label>

                <Form.Control
                  type="text"
                  {...register('pageSettings.recommendedTrips.1.title')}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="recommendedTrips-1-url">
                <Form.Label>URL</Form.Label>
                <Form.Control
                  type="url"
                  {...register('pageSettings.recommendedTrips.1.url')}
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="d-grid gap-2 py-3">
            <SubmitButton isSubmitting={isSubmitting || loading} block />
          </div>
        </AdminFeature>
      </Form>
    </>
  );
}
